import Vue from 'vue'
import Router, { Route } from 'vue-router'
import SignUp from '@/pages/SignUp.vue'
import Login from '@/pages/Login.vue'
import HomeLayout from '@/layouts/HomeLayout.vue'
import DashboardLayout from '@/layouts/DashboardLayout.vue'
import Setup from '@/pages/Setup.vue'
import ResetPassword from '@/pages/ResetPassword.vue'
import DashboardIndex from '@/pages/Dashboard/Index.vue'
import DashboardEvents from '@/pages/Dashboard/Events.vue'
import DashboardDoctors from '@/pages/Dashboard/Doctors.vue'
import DashboardCenters from '@/pages/Dashboard/Centers.vue'
import DashboardDrugs from '@/pages/Dashboard/Drugs.vue'
import DashboardEventStat from '@/pages/Dashboard/EventStat.vue'
import PatientCreate from '@/pages/Dashboard/PatientCreate.vue'
import EventCreate from '@/pages/Dashboard/EventCreate.vue'
import EventExport from '@/pages/Dashboard/EventExport.vue'
import PatientEdit from '@/pages/Dashboard/PatientEdit.vue'
import PatientList from '@/pages/Dashboard/PatientList.vue'
import PatientView from '@/pages/Dashboard/PatientView.vue'
import PatientProfile from '@/pages/DashboardPatient/Profile.vue'
import PageNotFound from '@/pages/PageNotFound.vue'
import { auth } from '@/store/modules'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/',
      component: HomeLayout,
      meta: {
        guest: true,
      },
      children: [
        {
          path: '/',
          redirect: '/login'
        },
        {
          path: '/signup',
          component: SignUp,
        },
        {
          path: '/login',
          component: Login,
        },
        {
          path: '/reset-password',
          component: ResetPassword,
        },
        {
          path: '/setup',
          component: Setup,
        },
      ]
    },
    {
      path: '/dashboard',
      component: DashboardLayout,
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          path: '/dashboard',
          redirect: '/dashboard/profile'
        },
        {
          path: '/dashboard/profile',
          component: DashboardIndex,
          meta: {
            titleKey: 'general.titles.settings'
          }
        },
        {
          path: '/dashboard/centers',
          components: {
            default: DashboardCenters
          },
          meta: {
            titleKey: 'general.titles.centers',
            requiresPrimaryManager: true,
            authType: 'doctor',
          }
        },
        {
          path: '/dashboard/doctors',
          components: {
            default: DashboardDoctors
          },
          meta: {
            titleKey: 'general.titles.doctors',
            authType: 'doctor',
          }
        },
        {
          path: '/dashboard/events/stat',
          component: DashboardEventStat,
          meta: {
            titleKey: 'general.titles.eventStat',
            authType: 'doctor',
          }
        },
        {
          path: '/dashboard/export/events',
          component: EventExport,
          meta: {
            titleKey: 'general.titles.eventExport',
            authType: 'doctor',
          }
        },
        {
          path: '/dashboard/create/event',
          component: EventCreate,
          meta: {
            titleKey: 'general.titles.eventCreate',
            authType: 'doctor',
          }
        },
        {
          path: '/dashboard/events',
          components: {
            default: DashboardEvents
          },
          meta: {
            titleKey: 'general.titles.events',
            authType: 'doctor',
          }
        },
        {
          path: '/dashboard/patients',
          components: {
            default: PatientList
          },
          meta: {
            titleKey: 'general.titles.patients',
            authType: 'doctor',
          }
        },
        {
          path: '/dashboard/patients/create',
          components: {
            default: PatientCreate
          },
          meta: {
            titleKey: 'general.titles.createPatient',
            authType: 'doctor',
          }
        },
        {
          path: '/dashboard/patients/:id',
          redirect: '/dashboard/patients/:id/events'
        },
        {
          path: '/dashboard/patients/:id/events',
          components: {
            default: PatientView
          },
          // props: { default: true, tab: 'events'}, 
          meta: {
            tab: 'events',
            titleKey: 'general.titles.patientProfile',
            authType: 'doctor',
          }
        },
        {
          path: '/dashboard/patients/:id/stock',
          components: {
            default: PatientView
          },
          // props: { default: true, tab: 'stock'}, 
          meta: {
            tab: 'stock',
            titleKey: 'general.titles.patientProfile',
            authType: 'doctor',
          }
        },
        {
          path: '/dashboard/patients/:id/edit',
          components: {
            default: PatientEdit
          },
          meta: {
            titleKey: 'general.titles.editPatient',
            authType: 'doctor',
          }
        },
        {
          path: '/dashboard/drugs',
          component: DashboardDrugs,
          meta: {
            titleKey: 'general.titles.drugs',
            authType: 'doctor',
          }
        },
      ]
    },
    { path: "*", component: PageNotFound }
  ]
})

router.beforeEach((to, from, next) => {
  // Authenticaed routes will lead to login screen when user is not logged in.
  if (to.matched.some(record => record.meta.requiresAuth) && !auth.isAuthenticated) {
    next({
      path: '/login',
      params: { nextUrl: to.fullPath }
    });
    return;
  }

  if (to.matched.some(record => record.meta.requiresPrimaryManager) && !auth.isPrimaryCenter) {
    next({
      path: '/dashboard',
      params: { nextUrl: to.fullPath }
    });
    return;
  }

  if (auth.isAuthenticated) {
    // Guest routes will be hijacked to dashboard if the user is logged in.
    if (to.matched.some(record => record.meta.guest)) {
      next({ path: '/dashboard' });
      return;
    }

    // Doctors will be redirected to doctor home when accessing patient route.
    if (to.matched.some(record => record.meta.authType === 'patient') && auth.isDoctor) {
      next({ path: '/dashboard' });
    }

    // Patients will be redirected to patient home when accessing doctor only page.
    if (to.matched.some(record => record.meta.authType === 'doctor') && auth.isPatient) {
      next({ path: '/dashboard' });
    }
  }

  next();
});

export default router;
